import { call, delay, race, select, take } from 'redux-saga/effects';
import { dispatch, getUrlParams } from '@restore/helpers';
import { SubmitReadingTaskAction } from '../formManagement.types';
import { readerTaskSlice } from '@restore/readerTask/readerTask.slice';
import {
  selectReadingTaskState,
  selectCurrentSubTaskState,
  selectFormManagementState,
  selectTabCoordinationState,
} from '../../utils';
import { startNextSubTask } from '@restore/readerTaskOrchestration/readerTaskOrchestration.slice';
import { formManagementSlice } from '../formManagement.slice';
import { goToRoute } from '@dynamika-shared/utils/sagaRouter';
import { prepareUrl } from 'src/utils/routerHelpers';
import { routesPath } from 'src/router/routes';
import { detachRpcScheme } from 'src/services/rpc/detach/scheme';
import { UserRoles } from '@dynamika-shared/types/auth';
import { getDetachedStorageKeys } from '@restore/readerViewerSession/tabCoordination/storageKeys';

// Upper bound on how long submit waits for completeReaderTask's outcome.
// formManagementSaga watches submitReadingTask with takeLeading (not
// takeLatest), so a worker that never returns permanently blocks every later
// submit click with no feedback to the user — see the race below.
export const COMPLETE_REQUEST_TIMEOUT = 30_000;

export function* submitReadingTaskWorker({
  payload: { value },
}: SubmitReadingTaskAction) {
  const { dataToSubmit, formDataCache, formIsValid } = yield* selectFormManagementState();
  const task = yield* selectReadingTaskState();
  const subTask = yield* selectCurrentSubTaskState();
  const { taskId: routeTaskId } = yield call(getUrlParams);
  const taskId = subTask?.id ?? task?.id ?? '';
  const clinicalTrialId = task?.clinicalTrialId ?? '';

  if (!taskId || !clinicalTrialId) {
    throw new Error('TaskId or ClinicalTrialId is not defined');
  }

  // Before clearing state for submit, remember current form values to be able to restore on error
  const prevFormData = formDataCache;
  const prevIsValid = formIsValid ?? false;

  yield dispatch(formManagementSlice.actions.setFormDirty(false));
  yield dispatch(
    formManagementSlice.actions.changeScoringFormData({
      formData: null,
      isValid: false,
    })
  );

  // Always avoid legacy redirect path; handle navigation below
  const act = readerTaskSlice.actions.completeReaderTask({
    data: {
      formData: dataToSubmit,
      signMessage: 'I confirm the data recorded and approve the report',
      ...value,
    },
    clinicalTrialId,
    taskId,
    shouldRedirect: false,
  });

  // Dispatch through the reader-task slice instead of invoking its worker
  // directly. The request action sets `readerTask.saving`, which disables both
  // submit buttons while the API call is in flight. Waiting for the matching
  // terminal action keeps this coordinator alive through navigation.
  yield dispatch(act);

  const { failure, timeout } = yield race({
    success: take(readerTaskSlice.actions.completeReaderTaskSuccess.type),
    failure: take(readerTaskSlice.actions.completeReaderTaskFailure.type),
    timeout: delay(COMPLETE_REQUEST_TIMEOUT),
  });

  yield dispatch(
    formManagementSlice.actions.setSubmitConfirmationModalOpened(false)
  );
  if (failure || timeout) {
    // Restore previous form state so the user can resubmit
    yield dispatch(
      formManagementSlice.actions.changeScoringFormData({
        formData: prevFormData,
        isValid: prevIsValid,
      })
    );
    // Ensure form is not marked dirty due to temporary clear
    yield dispatch(formManagementSlice.actions.setFormDirty(false));
    return;
  }

  // If we still have a subTask in context, move to the next one
  if (subTask?.id) {
    yield dispatch(
      startNextSubTask({
        clinicalTrialId,
        taskId: routeTaskId || task?.id || '',
        subTaskId: subTask.id,
      })
    );
    return;
  }

  // No next subtask -> behave like Cancel/Close
  const { mainTab } = yield* selectTabCoordinationState();

  const userRole: UserRoles | null = yield select(
    (state: { auth?: { data?: { selectedRole?: UserRoles } } }) =>
      state?.auth?.data?.selectedRole || null
  );

  const redirectRoute = userRole === UserRoles.EXTERNAL_USER
    ? routesPath.externalUserSubjectsClinicalTrial
    : routesPath.readerClinicalTrialReadings;

  const nextUrl = prepareUrl(
    redirectRoute,
    [
      clinicalTrialId,
      // keep optional taskId segment same as Footer readonly case (none here)
    ].filter(Boolean) as string[]
  );

  if (mainTab) {
    yield goToRoute(nextUrl);
  } else {
    yield call(detachRpcScheme.events.contextChanged, {
      reason: 'AFTER_SUBMIT_REDIRECT',
      clinicalTrialId,
      nextUrl,
    });
    // Preserve the close reason for this main session only.
    localStorage.setItem(
      getDetachedStorageKeys(window.name).afterSubmitRedirect,
      '1'
    );

    yield delay(1000);
    window.close();
  }
}
